import type { ShouldRevalidateFunctionArgs } from "react-router";

/**
 * True for a successful account-drawer sign-in or registration submit.
 *
 * Both `/login` and `/register` RETURN success data rather than redirecting
 * (SFCC-11869), so the page URL never changes (it stays e.g. `/cart`) and the
 * auth-boundary pathname checks in the callers never see the submit. This branch
 * is the only thing that re-runs the loaders driving signed-in chrome.
 *
 * Shared by `app/root.tsx` and `app/routes/layout.tsx` rather than inlined in
 * both, because inlining already cost us once: `/register` was added to root's
 * copy and missed in layout's, which left the cart guest-rendered while the
 * checkout hop fell back to its never-strand deadline. Extracted as its own
 * module for the same reason as `dispatcherShouldRevalidate` — one unit test
 * covers both call sites, and they cannot drift.
 */
export function isSuccessfulAuthSubmit({
  formMethod,
  formAction,
  actionStatus,
}: Pick<
  ShouldRevalidateFunctionArgs,
  "formMethod" | "formAction" | "actionStatus"
>): boolean {
  if (formMethod !== "POST") return false;
  if (!formAction?.includes("/login") && !formAction?.includes("/register")) {
    return false;
  }
  return actionStatus !== undefined && actionStatus < 400;
}

/**
 * True for the sign-out submit. D4 (P4) made `/logout` mutate only via its
 * POST action, and the SignOutButton block submits a navigation
 * `<Form method="post">` — in React Router a form POST never puts `/logout`
 * into `currentUrl`/`nextUrl`, it only appears as `formAction`. The URL-based
 * auth-boundary rules in root/layout (written for the pre-D4 GET navigation)
 * therefore never see the sign-out, and chrome kept rendering the user as
 * signed in until an unrelated action or same-URL navigation.
 *
 * Deliberately NOT gated on `actionStatus`, unlike `isSuccessfulAuthSubmit`:
 * the logout action responds with a redirect (there may be no action status to
 * inspect), and even a fail-soft teardown should refresh chrome rather than
 * keep stale signed-in data. Substring, not equality: the button posts via
 * `url("/logout")`, so `formAction` may carry a locale prefix
 * (e.g. `/en-us/logout`).
 */
export function isLogoutSubmit({
  formMethod,
  formAction,
}: Pick<ShouldRevalidateFunctionArgs, "formMethod" | "formAction">): boolean {
  return formMethod === "POST" && !!formAction?.includes("/logout");
}

/**
 * The COM-7 guest login page at its canonical URL (Release 2026.16 `/account`
 * flip). Exact: `/account/<sub>` is SFRA-proxied and never a React Router
 * navigation source.
 */
export const LOGIN_PAGE_PATHNAME = "/account";

/**
 * True when a navigation LEAVES the `/account` login page for another path —
 * an auth boundary exactly like the retired standalone `/login` page was.
 *
 * Why the auth-submit branch alone is not enough there: `/login` returns
 * success DATA, so React Router revalidates every active loader with the fresh
 * signed-in cookies. On `/account` one of those active loaders is the login
 * page's own, and for a now-authenticated shopper it THROWS a redirect to
 * `/my-account` (SFRA parity: Login-Show bounces to Account-Show). React Router
 * treats a loader redirect during post-action revalidation as a short circuit:
 * it starts the redirect navigation and commits NONE of that round's loader
 * data — root and layout included. The redirect navigation then asks
 * `shouldRevalidate` again, this time with no form submission, and the plain
 * "cross-page GET does not revalidate the expensive chrome loaders" rule would
 * keep the header rendering the shopper as a guest until a full document load
 * (measured on fsa.devhec.com, 2026-09-22: sign in on /account → dashboard
 * renders, header still says "Sign in"; SPA nav home keeps it stale; reload
 * fixes it). The account DRAWER never hits this because the page behind it has
 * no loader that redirects on auth, so its revalidation round completes.
 *
 * Also fires for a guest who leaves `/account` without signing in — one extra
 * chrome load on one hop, the same cost the old `/login` page carried.
 */
export function isLeavingLoginPage({
  currentUrl,
  nextUrl,
}: Pick<ShouldRevalidateFunctionArgs, "currentUrl" | "nextUrl">): boolean {
  return (
    currentUrl.pathname === LOGIN_PAGE_PATHNAME &&
    nextUrl.pathname !== LOGIN_PAGE_PATHNAME
  );
}

/**
 * The single auth-submit revalidation gate shared by `app/root.tsx` and
 * `app/routes/layout.tsx`: success-gated for login/register, ungated for
 * logout. Keep BOTH consumers on this wrapper (not the individual predicates)
 * for the same anti-drift reason documented on `isSuccessfulAuthSubmit`.
 */
export function isAuthRevalidatingSubmit(
  args: Pick<
    ShouldRevalidateFunctionArgs,
    "formMethod" | "formAction" | "actionStatus"
  >,
): boolean {
  return isSuccessfulAuthSubmit(args) || isLogoutSubmit(args);
}
